import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  useMediaQuery,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People,
  RestaurantMenu,
  LocalShipping,
  ShoppingCart,
  Assessment,
  Store,
  ExitToApp,
  Settings,
  Palette,
  MonitorHeart,
  TableRestaurant,
  Print,
  LocalOffer,
  LocationOn,
  QrCode2,
  ChevronLeft,
  ChevronRight,
  HelpOutline,
  Menu as MenuIcon,
  History as HistoryIcon,
  Schedule as ScheduleIcon,
  Tune as TuneIcon,
  Insights as InsightsIcon,
} from '@mui/icons-material';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width:768px)');
  const isTablet = useMediaQuery('(min-width:769px) and (max-width:1024px)');

  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window !== 'undefined' && window.innerWidth <= 1024) return true;
    const saved = localStorage.getItem('admin_sidebar_collapsed');
    return saved === 'true';
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleToggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem('admin_sidebar_collapsed', String(next));
  };

  const effectiveCollapsed = isMobile ? false : (isTablet ? true : collapsed);
  const sidebarWidth = effectiveCollapsed ? 72 : 240;
  const accentColor = '#00C896';

  const menuItems = [
    { path: '/', label: t('sidebar.dashboard'), icon: <DashboardIcon /> },
    { path: '/users', label: t('sidebar.usersManagement'), icon: <People /> },
    { path: '/customers', label: t('sidebar.customerUsers'), icon: <People /> },
    { path: '/menu', label: t('sidebar.menuManagement'), icon: <RestaurantMenu /> },
    { path: '/modifiers', label: t('sidebar.modifiers', 'Modifier Groups'), icon: <TuneIcon /> },
    { path: '/orders', label: t('sidebar.ordersMonitoring'), icon: <ShoppingCart /> },
    { path: '/monitoring', label: t('sidebar.staffMonitoring'), icon: <MonitorHeart /> },
    { path: '/admin-daily-closing', label: t('sidebar.dailyClosingReports'), icon: <Assessment /> },
    { path: '/branches', label: t('sidebar.branches'), icon: <Store /> },
    { path: '/drivers', label: t('sidebar.driversManagement'), icon: <LocalShipping /> },
    { path: '/tables', label: t('sidebar.tablesManagement'), icon: <TableRestaurant /> },
    { path: '/theme', label: t('sidebar.themeManagement'), icon: <Palette /> },
    { path: '/printer-routing', label: 'Printer Routing', icon: <Print /> },
    { path: '/delivery-zones', label: 'Delivery Zones', icon: <LocationOn /> },
    { path: '/discounts', label: 'Discount Monitoring', icon: <LocalOffer /> },
    { path: '/audit-log', label: 'Audit Log', icon: <HistoryIcon /> },
    { path: '/shifts', label: 'Staff Shifts', icon: <ScheduleIcon /> },
    { path: '/qr-codes', label: 'QR Codes', icon: <QrCode2 /> },
    { path: '/marketing', label: t('sidebar.marketing', 'Marketing & QR Tracking'), icon: <InsightsIcon /> },
    { path: '/reports', label: t('sidebar.reportsAnalytics'), icon: <Assessment /> },
  ];

  const isActive = (path: string) => location.pathname === path;

  const handleNavClick = (path: string) => {
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  // Shared sidebar content
  const sidebarContent = (forMobile: boolean) => {
    const showLabels = forMobile ? true : !effectiveCollapsed;
    return (
      <>
        {/* Logo + Toggle */}
        <Box sx={{
          px: showLabels ? 2 : 0,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: showLabels ? 'flex-start' : 'center',
          gap: showLabels ? 1.2 : 0,
          minHeight: 60,
          borderBottom: '1px solid #1e2139',
        }}>
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }}>
            <img src="/saheeh_logo_w.svg" alt="Saheeh"
              style={{ width: showLabels ? 158 : 44, height: showLabels ? 'auto' : 44, objectFit: 'contain' }} />
          </Box>
          {!forMobile && (
            <IconButton onClick={handleToggle} size="small" sx={{ color: '#6b7280', width: 26, height: 26, ml: showLabels ? 0 : 0, '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,0.05)' } }}>
              {effectiveCollapsed ? <ChevronRight sx={{ fontSize: 16 }} /> : <ChevronLeft sx={{ fontSize: 16 }} />}
            </IconButton>
          )}
        </Box>

        {/* User Info */}
        {showLabels && (
          <Box sx={{ px: 2, py: 1.2, display: 'flex', alignItems: 'center', gap: 1.2, borderBottom: '1px solid #1e2139' }}>
            <Avatar sx={{ bgcolor: accentColor, color: '#1a1d2e', width: 30, height: 30, fontSize: '12px', fontWeight: 700 }}>A</Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ color: '#e0e0e0', fontWeight: 600, fontSize: '0.78rem', fontFamily: '"Poppins"', lineHeight: 1.2 }}>Admin</Typography>
              <Typography sx={{ color: '#6b7280', fontSize: '0.65rem', fontFamily: '"Poppins"', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>admin@saheehpos.com</Typography>
            </Box>
          </Box>
        )}

        {/* Nav Items */}
        <Box sx={{ flex: 1, py: 0.5, px: 0.5, overflowY: 'auto', '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { background: '#2a2d3e', borderRadius: 2 } }}>
          {menuItems.map((item) => {
            const active = isActive(item.path);
            return (
              <Tooltip title={!showLabels ? item.label : ''} placement="right" key={item.path}>
                <ListItem
                  onClick={() => handleNavClick(item.path)}
                  sx={{
                    borderRadius: '8px', mb: 0.15, mx: showLabels ? 0.75 : 0.5, py: 0.7, px: showLabels ? 1.5 : 1.2,
                    justifyContent: showLabels ? 'flex-start' : 'center', minHeight: 38,
                    bgcolor: active ? 'rgba(0, 200, 150,0.12)' : 'transparent',
                    color: active ? accentColor : '#9ca3af',
                    borderLeft: active ? `3px solid ${accentColor}` : '3px solid transparent',
                    cursor: 'pointer', transition: 'all 0.12s ease',
                    '&:hover': { bgcolor: active ? 'rgba(0, 200, 150,0.15)' : 'rgba(255,255,255,0.04)', color: active ? accentColor : '#e0e0e0',
                      '& .MuiListItemIcon-root': { color: active ? accentColor : '#e0e0e0' } },
                  }}
                >
                  <ListItemIcon sx={{ color: active ? accentColor : '#6b7280', minWidth: showLabels ? 34 : 0, justifyContent: 'center', '& .MuiSvgIcon-root': { fontSize: 19 } }}>
                    {item.icon}
                  </ListItemIcon>
                  {showLabels && (
                    <ListItemText primary={item.label} sx={{ '& .MuiListItemText-primary': { fontSize: '0.8rem', fontWeight: active ? 600 : 400, fontFamily: '"Poppins"', color: 'inherit', whiteSpace: 'nowrap' } }} />
                  )}
                </ListItem>
              </Tooltip>
            );
          })}
        </Box>

        {/* Bottom */}
        <Box sx={{ borderTop: '1px solid #1e2139', py: 0.5, px: 0.5 }}>
          <Tooltip title={!showLabels ? 'Help' : ''} placement="right">
            <ListItem sx={{ borderRadius: '8px', mx: showLabels ? 0.75 : 0.5, py: 0.7, px: showLabels ? 1.5 : 1.2, justifyContent: showLabels ? 'flex-start' : 'center', minHeight: 38, color: '#6b7280', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,255,255,0.04)', color: '#e0e0e0' } }}>
              <ListItemIcon sx={{ color: 'inherit', minWidth: showLabels ? 34 : 0, justifyContent: 'center', '& .MuiSvgIcon-root': { fontSize: 19 } }}><HelpOutline /></ListItemIcon>
              {showLabels && <ListItemText primary="Help" sx={{ '& .MuiListItemText-primary': { fontSize: '0.8rem', fontFamily: '"Poppins"', color: 'inherit' } }} />}
            </ListItem>
          </Tooltip>
          <Tooltip title={!showLabels ? t('sidebar.settings') : ''} placement="right">
            <ListItem onClick={() => handleNavClick('/settings')} sx={{ borderRadius: '8px', mx: showLabels ? 0.75 : 0.5, py: 0.7, px: showLabels ? 1.5 : 1.2, justifyContent: showLabels ? 'flex-start' : 'center', minHeight: 38, color: '#6b7280', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,255,255,0.04)', color: '#e0e0e0' } }}>
              <ListItemIcon sx={{ color: 'inherit', minWidth: showLabels ? 34 : 0, justifyContent: 'center', '& .MuiSvgIcon-root': { fontSize: 19 } }}><Settings /></ListItemIcon>
              {showLabels && <ListItemText primary={t('sidebar.settings')} sx={{ '& .MuiListItemText-primary': { fontSize: '0.8rem', fontFamily: '"Poppins"', color: 'inherit' } }} />}
            </ListItem>
          </Tooltip>
          <Tooltip title={!showLabels ? t('sidebar.logout') : ''} placement="right">
            <ListItem onClick={() => { localStorage.clear(); navigate('/login'); }} sx={{ borderRadius: '8px', mx: showLabels ? 0.75 : 0.5, py: 0.7, px: showLabels ? 1.5 : 1.2, justifyContent: showLabels ? 'flex-start' : 'center', minHeight: 38, color: '#ef4444', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(239,68,68,0.1)' } }}>
              <ListItemIcon sx={{ color: '#ef4444', minWidth: showLabels ? 34 : 0, justifyContent: 'center', '& .MuiSvgIcon-root': { fontSize: 19 } }}><ExitToApp /></ListItemIcon>
              {showLabels && <ListItemText primary={t('sidebar.logout')} sx={{ '& .MuiListItemText-primary': { fontSize: '0.8rem', fontWeight: 600, fontFamily: '"Poppins"', color: 'inherit' } }} />}
            </ListItem>
          </Tooltip>
        </Box>
      </>
    );
  };

  return (
    <Box sx={{ display: 'flex', height: '100vh', bgcolor: '#1a1d2e' }}>
      {/* Mobile: Drawer */}
      {isMobile ? (
        <>
          <Drawer
            open={mobileOpen}
            onClose={() => setMobileOpen(false)}
            PaperProps={{ sx: { width: 260, bgcolor: '#0A2540', border: 'none' } }}
            ModalProps={{ keepMounted: true }}
          >
            {sidebarContent(true)}
          </Drawer>

          {/* Mobile top bar */}
          <Box sx={{
            position: 'fixed', top: 0, left: 0, right: 0, height: 56,
            bgcolor: '#0A2540', borderBottom: '1px solid #1e2139',
            display: 'flex', alignItems: 'center', px: 1.5, zIndex: 1100, gap: 1.5,
          }}>
            <IconButton onClick={() => setMobileOpen(true)} sx={{ color: accentColor }}>
              <MenuIcon />
            </IconButton>
            <Box component="img" src="/saheeh_logo_w.svg" alt="Saheeh" sx={{ height: 36, width: 'auto' }} />
          </Box>

          {/* Main content */}
          <Box component="main" sx={{
            flexGrow: 1, mt: '56px', bgcolor: '#1e2139',
            overflow: 'auto', WebkitOverflowScrolling: 'touch',
            display: 'flex', flexDirection: 'column',
            minHeight: 'calc(100vh - 56px)',
          }}>
            <Box sx={{ flex: 1 }}>
              {children}
            </Box>
            {/* Mobile footer */}
            <Box sx={{ textAlign: 'center', py: 3, pb: 5, borderTop: '1px solid #2a2d3e', bgcolor: '#0A2540' }}>
              <Box component="img" src="/saheeh_logo_w.svg" alt="Saheeh" sx={{ height: 24, width: 'auto', mx: 'auto', mb: 0.5, opacity: 0.5 }} />
              <Box sx={{ fontSize: '0.6rem', color: '#4a4d62', fontFamily: '"Poppins"', letterSpacing: '0.5px' }}>
                Saheeh Admin v2.1 &middot; saheehpos.com
              </Box>
            </Box>
          </Box>
        </>
      ) : (
        <>
          {/* Desktop/Tablet: Fixed sidebar */}
          <Drawer
            variant="permanent"
            sx={{
              width: sidebarWidth, flexShrink: 0, transition: 'width 0.2s ease',
              [`& .MuiDrawer-paper`]: {
                width: sidebarWidth, boxSizing: 'border-box', bgcolor: '#0A2540',
                borderRight: '1px solid #1e2139', boxShadow: 'none',
                transition: 'width 0.2s ease', overflowX: 'hidden', overflowY: 'auto',
                '&::-webkit-scrollbar': { width: 4 },
                '&::-webkit-scrollbar-thumb': { background: '#2a2d3e', borderRadius: 2 },
              },
            }}
          >
            {sidebarContent(false)}
          </Drawer>

          {/* Main content */}
          <Box component="main" sx={{ flexGrow: 1, bgcolor: '#1e2139', overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
            {children}
          </Box>
        </>
      )}
    </Box>
  );
};

export default AdminLayout;
